import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useSelector } from "react-redux";
import {
	Background,
	BaseEdge,
	Controls,
	Edge as FlowEdge,
	MarkerType,
	MiniMap,
	Node as FlowNode,
	ReactFlow,
	ReactFlowProvider,
	ConnectionLineType,
	type EdgeProps
} from "@xyflow/react";
import { FiFilter, FiRefreshCw, FiSearch, FiInfo, FiX } from "react-icons/fi";
import "@xyflow/react/dist/style.css";
import type { ConnectionGraphViewModel, PageConnectionEdge, PageConnectionNode, Taxonomie } from "../API/Models";
import { client } from "../Clients";
import { usePageHeader } from "../hooks/usePageHeader";
import { LoadingSpinner } from "../Components/LoadingSpinner";
import { connectionNodeTypes } from "../Components/PageConnectionsGraph";
import { TaxonomyMultiSelect } from "../Components/TaxonomyMultiSelect";
import type { IApplicationState } from "../Redux/Reducers";

type ExtendedGraphNode = PageConnectionNode & { kind?: string | null };

const COLUMN_STEP = 260;
const ROW_STEP = 120;

const buildColumnPositions = (count: number): number[] => {
	if (count === 0) return [];
	if (count === 1) return [0];
	const start = -((count - 1) * ROW_STEP) / 2;
	return new Array(count).fill(0).map((_, idx) => start + idx * ROW_STEP);
};

const MAX_PAGE_DEPTH = 8;
const depthForNode = (node?: ExtendedGraphNode | null) => {
	if (!node?.slug) return 0;
	const depth = node.slug.split("/").filter(Boolean).length;
	return Math.min(depth, MAX_PAGE_DEPTH);
};

const formatLabel = (node?: ExtendedGraphNode | null) => node?.title || node?.slug || node?.id || "Untitled";
const formatSubtitle = (node?: ExtendedGraphNode | null) => {
	const slug = node?.slug ? `/${node.slug}` : "";
	const lang = node?.language ? node.language.toUpperCase() : "";
	if (slug && lang) return `${slug} • ${lang}`;
	return slug || lang || "";
};

const variantFromNode = (node?: ExtendedGraphNode | null): "main" | "incoming" | "outgoing" | "external" | "missing" | "layout" => {
	const inferredKind =
		node?.kind ??
		(node?.slug === "layout" ? "layout" : undefined) ??
		(node?.title?.toLowerCase().includes("layout") ? "layout" : undefined);
	const kind = inferredKind?.toLowerCase();
	if (kind === "layout") return "layout";
	if (kind === "external") return "external";
	if (kind === "missing") return "missing";
	return "outgoing";
};

const keyForNode = (node?: ExtendedGraphNode | null) =>
	`node:${node?.kind ?? "page"}:${node?.id ?? node?.slug ?? node?.title ?? Math.random().toString(36).slice(2)}`;

const EDGE_GAP = 14;
const arrow = (color: string) => ({ type: MarkerType.ArrowClosed, width: 12, height: 12, color });
const ShortStraightEdge: React.FC<EdgeProps> = (props) => {
	const {
		sourceX,
		sourceY,
		targetX,
		targetY,
		markerEnd,
		markerStart,
		style,
		interactionWidth
	} = props;

	const dx = targetX - sourceX;
	const dy = targetY - sourceY;
	const len = Math.sqrt(dx * dx + dy * dy) || 1;
	const gapX = (EDGE_GAP * dx) / len;
	const gapY = (EDGE_GAP * dy) / len;

	const sx = sourceX + gapX;
	const sy = sourceY + gapY;
	const tx = targetX - gapX;
	const ty = targetY - gapY;

	const path = `M ${sx},${sy} L ${tx},${ty}`;

	return <BaseEdge path={path} markerEnd={markerEnd} markerStart={markerStart} style={style} interactionWidth={interactionWidth} />;
};

/** Interactive page connections graph with language and content type filtering. */
const ConnectionsGraph: React.FC<{ embedded?: boolean }> = ({ embedded = false }) => {
	const [graph, setGraph] = useState<ConnectionGraphViewModel | null>(null);
	const [loading, setLoading] = useState(false);
	const [error, setError] = useState<string | null>(null);
	const [languageFilter, setLanguageFilter] = useState<string>("all");
	const [search, setSearch] = useState("");
	const [showDetails, setShowDetails] = useState(false);
	const [includeDynamic, setIncludeDynamic] = useState(false);
	const [dynamicTypeFilter, setDynamicTypeFilter] = useState<string[]>([]);
	const contentTypeConfigs = useSelector((state: IApplicationState) => state.contentTypeConfigs.items);

	const loadGraph = useCallback(
		async (lang?: string) => {
			setLoading(true);
			setError(null);
			try {
				const response = await client.connections.apiConnectionsPageGraphGet(
					lang && lang !== "all" ? lang : undefined,
					includeDynamic,
					includeDynamic && dynamicTypeFilter.length > 0 ? dynamicTypeFilter.join(",") : undefined
				);
				setGraph(response.data ?? null);
			} catch (err) {
				console.error(err);
				setError("Unable to load connection graph right now.");
			} finally {
				setLoading(false);
			}
		},
		[includeDynamic, dynamicTypeFilter]
	);

	useEffect(() => {
		void loadGraph(languageFilter);
	}, [languageFilter, includeDynamic, dynamicTypeFilter, loadGraph]);

	const nodesRaw = useMemo(() => (graph?.nodes as ExtendedGraphNode[] | undefined) ?? [], [graph?.nodes]);
	const edgesRaw = useMemo(() => graph?.edges ?? [], [graph?.edges]);

	const languages = useMemo(() => {
		const set = new Set<string>();
		nodesRaw.forEach((node) => {
			if (node.language) {
				set.add(node.language.toLowerCase());
			}
		});
		return Array.from(set).sort();
	}, [nodesRaw]);

	const dynamicTypeOptions = useMemo(() => {
		return (contentTypeConfigs ?? [])
			.filter((item) => item?.id)
			.map(
				(item) =>
					({
						id: item.id ?? "",
						title: item.title ?? item.id ?? ""
					}) as Taxonomie
			);
	}, [contentTypeConfigs]);

	const searchTerm = search.trim().toLowerCase();

	const filteredNodes = useMemo(() => {
		return nodesRaw.filter((node) => {
			const langOk =
				languageFilter === "all" ||
				(node.language ?? "").toLowerCase() === languageFilter.toLowerCase();
			if (!langOk) return false;
			if (!searchTerm) return true;
			const haystack = `${node.title ?? ""} ${node.slug ?? ""} ${node.id ?? ""}`.toLowerCase();
			return haystack.includes(searchTerm);
		});
	}, [nodesRaw, languageFilter, searchTerm]);

	const nodeKeys = useMemo(() => new Set(filteredNodes.map((n) => keyForNode(n))), [filteredNodes]);

	const filteredEdges = useMemo(() => {
		return edgesRaw.filter((edge) => {
			const fromKey = keyForNode(edge.from as ExtendedGraphNode);
			const toKey = keyForNode(edge.to as ExtendedGraphNode);
			return nodeKeys.has(fromKey) && nodeKeys.has(toKey);
		});
	}, [edgesRaw, nodeKeys]);

	const connectionCounts = useMemo(() => {
		const map = new Map<string, number>();
		filteredEdges.forEach((edge) => {
			const fromKey = keyForNode(edge.from as ExtendedGraphNode);
			const toKey = keyForNode(edge.to as ExtendedGraphNode);
			map.set(fromKey, (map.get(fromKey) ?? 0) + 1);
			map.set(toKey, (map.get(toKey) ?? 0) + 1);
		});
		return map;
	}, [filteredEdges]);

	const unconnectedPages = useMemo(() => {
		return filteredNodes.filter((node) => {
			const kind = node.kind?.toLowerCase() ?? "page";
			if (kind !== "page") return false;
			const degree = connectionCounts.get(keyForNode(node)) ?? 0;
			return degree === 0;
		});
	}, [filteredNodes, connectionCounts]);

	const maxPageDepth = useMemo(
		() => filteredNodes.reduce((max, node) => Math.max(max, depthForNode(node)), 0),
		[filteredNodes]
	);

	const groupOrder = useMemo(() => {
		const pages = Array.from({ length: maxPageDepth + 1 }, (_, idx) => `page-${idx}`);
		return ["layout", ...pages, "missing", "external"];
	}, [maxPageDepth]);

	const groupedNodes = useMemo(() => {
		const grouped = new Map<string, ExtendedGraphNode[]>();
		filteredNodes.forEach((node) => {
			const inferredKind =
				node.kind ??
				(node.slug === "layout" ? "layout" : undefined) ??
				(node.title?.toLowerCase().includes("layout") ? "layout" : undefined) ??
				"page";
			const kind = inferredKind.toLowerCase();
			const depth = kind === "page" ? depthForNode(node) : 0;
			const key = kind === "page" ? `page-${depth}` : kind;
			const bucket = grouped.get(key) ?? [];
			bucket.push(node);
			grouped.set(key, bucket);
		});

		grouped.forEach((bucket, key) => {
			const sorted = [...bucket].sort((a, b) => {
				const left = a.slug || a.title || "";
				const right = b.slug || b.title || "";
				return left.localeCompare(right);
			});
			grouped.set(key, sorted);
		});

		return grouped;
	}, [filteredNodes]);

	const flowNodes: FlowNode[] = useMemo(() => {
		const nodes: FlowNode[] = [];
		groupOrder.forEach((groupKey, index) => {
			const bucket = groupedNodes.get(groupKey);
			if (!bucket?.length) return;
			const ys = buildColumnPositions(bucket.length);
			bucket.forEach((node, idx) => {
				const variant = variantFromNode(node);
				nodes.push({
					id: keyForNode(node),
					type: "connection",
					position: { x: index * COLUMN_STEP, y: ys[idx] },
					data: {
						title: formatLabel(node),
						subtitle: formatSubtitle(node),
						variant: variant === "outgoing" ? "incoming" : variant
					}
				});
			});
		});
		return nodes;
	}, [groupedNodes]);

	const nodeIdSet = useMemo(() => new Set(flowNodes.map((n) => n.id)), [flowNodes]);

	const flowEdges: FlowEdge[] = useMemo(() => {
		type EdgeMeta = { sourceId: string; targetId: string; exists: boolean };
		const buckets = new Map<string, { forward: EdgeMeta[]; reverse: EdgeMeta[] }>();

		filteredEdges.forEach((edge) => {
			const sourceId = keyForNode(edge.from as ExtendedGraphNode);
			const targetId = keyForNode(edge.to as ExtendedGraphNode);
			if (!nodeIdSet.has(sourceId) || !nodeIdSet.has(targetId)) return;

			const exists = edge.exists !== false && (edge.to as ExtendedGraphNode | null)?.kind?.toLowerCase() !== "missing";
			const pairKey = sourceId < targetId ? `${sourceId}|${targetId}` : `${targetId}|${sourceId}`;
			const direction = sourceId <= targetId ? "forward" : "reverse";
			const bucket = buckets.get(pairKey) ?? { forward: [], reverse: [] };
			const meta: EdgeMeta = { sourceId, targetId, exists };
			bucket[direction === "forward" ? "forward" : "reverse"].push(meta);
			buckets.set(pairKey, bucket);
		});

		const built: FlowEdge[] = [];
		buckets.forEach((bucket, pairKey) => {
			const hasForward = bucket.forward.length > 0;
			const hasReverse = bucket.reverse.length > 0;

			if (hasForward && hasReverse) {
				const hasMissing = [...bucket.forward, ...bucket.reverse].some((e) => !e.exists);
				const stroke = hasMissing ? "#e11d48" : "#0f172a";
				const dash = hasMissing ? "6 4" : undefined;
				const sourceId = bucket.forward[0]?.sourceId ?? bucket.reverse[0]?.targetId;
				const targetId = bucket.forward[0]?.targetId ?? bucket.reverse[0]?.sourceId;

				built.push({
					id: `edge-bidirectional-${pairKey}`,
					source: sourceId,
					target: targetId,
					markerStart: arrow(stroke),
					markerEnd: arrow(stroke),
					style: {
						stroke,
						strokeWidth: 2,
						strokeDasharray: dash
					},
					type: "short"
				});
				return;
			}

			const singleDirection = hasForward ? bucket.forward : bucket.reverse;
			singleDirection.forEach((meta, idx) => {
				const stroke = meta.exists ? "#0f172a" : "#e11d48";
				built.push({
					id: `edge-${pairKey}-${hasForward ? "f" : "r"}-${idx}`,
					source: meta.sourceId,
					target: meta.targetId,
					markerEnd: arrow(stroke),
					style: {
						stroke,
						strokeWidth: 2,
						strokeDasharray: meta.exists ? undefined : "6 4"
					},
					type: "short"
				});
			});
		});

		return built;
	}, [filteredEdges, nodeIdSet]);

	const stats = useMemo(
		() => ({
			nodes: filteredNodes.length,
			edges: filteredEdges.length,
			unconnected: unconnectedPages.length,
			external: filteredNodes.filter((n) => variantFromNode(n) === "external").length,
			layout: filteredNodes.filter((n) => variantFromNode(n) === "layout").length,
			missing: filteredNodes.filter((n) => variantFromNode(n) === "missing").length
		}),
		[filteredNodes, filteredEdges.length, unconnectedPages.length]
	);

	const headerActions = useMemo(
		() => (
			<div className="flex flex-wrap items-center gap-2">
				<input
					className="w-64 rounded-lg border border-slate-200 px-3 py-2 text-sm text-slate-700 focus:border-slate-400 focus:outline-none"
					placeholder="Search by title or slug"
					value={search}
					onChange={(e) => setSearch(e.target.value)}
				/>
				<select
					className="rounded-lg border border-slate-200 px-3 py-2 text-sm text-slate-700 focus:border-slate-400 focus:outline-none"
					value={languageFilter}
					onChange={(e) => setLanguageFilter(e.target.value)}
				>
					<option value="all">All languages</option>
					{languages.map((lang) => (
						<option key={lang} value={lang}>
							{lang.toUpperCase()}
						</option>
					))}
				</select>
				<label className="inline-flex items-center gap-2 text-xs font-semibold text-slate-600">
					<input
						type="checkbox"
						checked={includeDynamic}
						onChange={(e) => setIncludeDynamic(e.target.checked)}
						className="h-4 w-4 rounded border-slate-300 text-slate-900 focus:ring-slate-400"
					/>
					Load dynamic content
				</label>
				<div className="min-w-[240px] max-w-[320px]">
					<TaxonomyMultiSelect
						label="Dynamic content types"
						placeholder="All dynamic types"
						options={dynamicTypeOptions}
						value={dynamicTypeFilter}
						onChange={setDynamicTypeFilter}
						disabled={!includeDynamic}
						maxVisibleOptions={40}
					/>
				</div>
				<div className="ml-auto flex flex-wrap items-center gap-2">
					<button
						type="button"
						onClick={() => void loadGraph(languageFilter)}
						className="inline-flex items-center gap-2 rounded-lg border border-slate-200 bg-white px-3 py-2 text-sm font-semibold text-slate-700 transition hover:border-slate-300 hover:bg-slate-50"
					>
						<FiRefreshCw /> Refresh
					</button>
					<button
						type="button"
						onClick={() => setShowDetails(true)}
						className="inline-flex items-center gap-2 rounded-lg bg-slate-900 px-3 py-2 text-sm font-semibold text-white transition hover:bg-slate-800"
					>
						<FiInfo /> Details
					</button>
				</div>
			</div>
		),
		[languageFilter, languages, loadGraph, search, includeDynamic, dynamicTypeFilter, dynamicTypeOptions]
	);

	// When embedded in a modal we must not hijack the global page header — the actions are rendered
	// inline in the toolbar below instead.
	usePageHeader({
		title: "Connection graph",
		actions: headerActions,
		enabled: !embedded
	});

	const detailsPanel = (
		<div className="space-y-4">
			<div>
				<p className="text-xs uppercase tracking-[0.3em] text-slate-400">Summary</p>
				<div className="mt-3 grid grid-cols-2 gap-3 text-sm">
					<div className="rounded-xl border border-slate-100 bg-slate-50 px-3 py-2 font-semibold text-slate-800">Nodes: {stats.nodes}</div>
					<div className="rounded-xl border border-slate-100 bg-slate-50 px-3 py-2 font-semibold text-slate-800">Edges: {stats.edges}</div>
					<div className="rounded-xl border border-slate-100 bg-slate-50 px-3 py-2 font-semibold text-slate-800">Layout nodes: {stats.layout}</div>
					<div className="rounded-xl border border-slate-100 bg-slate-50 px-3 py-2 font-semibold text-slate-800">External targets: {stats.external}</div>
					<div className="rounded-xl border border-slate-100 bg-slate-50 px-3 py-2 font-semibold text-slate-800">Missing targets: {stats.missing}</div>
					<div className="rounded-xl border border-amber-200 bg-amber-50 px-3 py-2 font-semibold text-amber-800">Unconnected pages: {stats.unconnected}</div>
				</div>
			</div>

			<div className="rounded-2xl border border-slate-200 bg-white/90 p-4 shadow-inner">
				<div className="flex items-center gap-2 text-xs font-semibold uppercase tracking-[0.25em] text-slate-500">
					<FiFilter /> Unconnected pages
				</div>
				{unconnectedPages.length === 0 ? (
					<p className="mt-2 text-sm text-slate-500">Every page is linked somewhere.</p>
				) : (
					<ul className="mt-3 space-y-2 text-sm text-slate-800">
						{unconnectedPages.map((node) => (
							<li
								key={keyForNode(node)}
								className="rounded-lg border border-amber-200 bg-amber-50 px-3 py-2"
							>
								<p className="font-semibold">{formatLabel(node)}</p>
								<p className="text-xs text-slate-500">{formatSubtitle(node) || "No slug provided"}</p>
							</li>
						))}
					</ul>
				)}
			</div>

			<div className="rounded-2xl border border-slate-200 bg-white/90 p-4 shadow-inner space-y-2">
				<div className="flex items-center gap-2 text-xs font-semibold uppercase tracking-[0.25em] text-slate-500">
					<FiSearch /> Legend
				</div>
				<div className="flex flex-wrap gap-2 text-xs text-slate-600">
					<span className="inline-flex items-center gap-1 rounded-full border border-slate-200 bg-white px-2 py-1">
						<span className="h-3 w-3 rounded-full border border-slate-900 bg-slate-900"></span> Page
					</span>
					<span className="inline-flex items-center gap-1 rounded-full border border-slate-200 bg-white px-2 py-1">
						<span className="h-3 w-3 rounded-full border border-indigo-500 bg-indigo-100"></span> Layout
					</span>
					<span className="inline-flex items-center gap-1 rounded-full border border-slate-200 bg-white px-2 py-1">
						<span className="h-3 w-3 rounded-full border border-sky-500 bg-sky-100"></span> External
					</span>
					<span className="inline-flex items-center gap-1 rounded-full border border-rose-300 bg-rose-50 px-2 py-1">
						<span className="h-3 w-3 rounded-full border border-rose-500 bg-rose-100"></span> Missing
					</span>
				</div>
			</div>
		</div>
	);

	return (
		<div className={embedded ? "flex h-full min-h-0 flex-col space-y-3" : "flex h-full min-h-0 flex-col space-y-4 p-6"}>
			{/* When embedded the global page header is suppressed, so surface the filter/search actions here. */}
			{embedded && headerActions && <div className="flex flex-wrap items-center gap-2">{headerActions}</div>}
			{error && (
				<div className="rounded-xl border border-rose-200 bg-rose-50 px-4 py-2 text-sm text-rose-700">{error}</div>
			)}

			<div
				className={`relative flex-1 min-h-[520px] rounded-2xl border border-slate-200 bg-white shadow-sm overflow-hidden ${
					embedded ? "" : "h-[calc(100vh-220px)]"
				}`}
			>
				{loading && (
					<div className="absolute inset-0 z-40 flex items-center justify-center bg-card">
						<LoadingSpinner size="lg" />
					</div>
				)}
				<div className="absolute inset-0">
					<ReactFlowProvider>
						<ReactFlow
							nodes={flowNodes}
							edges={flowEdges}
							nodeTypes={connectionNodeTypes}
							className="h-full w-full"
							fitView
							fitViewOptions={{ padding: 0.35 }}
							minZoom={0.2}
							maxZoom={1.4}
							panOnScroll
							zoomOnScroll
							attributionPosition="bottom-right"
							defaultEdgeOptions={{ type: "short" }}
							connectionLineType={ConnectionLineType.Straight}
							edgeTypes={{ short: ShortStraightEdge }}
						>
							<Background gap={20} size={1} color="#e2e8f0" />
							<MiniMap
								pannable
								zoomable
								position="bottom-left"
								className="bg-white/90 border border-slate-200 shadow-sm rounded-md"
								style={{ left: 12, bottom: 12 }}
							/>
							<Controls
								showInteractive={false}
								position="bottom-right"
								className="bg-white/90 border border-slate-200 shadow-sm rounded-md"
								style={{ right: 12, bottom: 12 }}
							/>
						</ReactFlow>
					</ReactFlowProvider>
				</div>
			</div>

			{showDetails && (
				<div className="fixed inset-0 z-50 flex items-center justify-center bg-slate-900/60 p-4">
					<div className="w-full max-w-4xl rounded-2xl bg-white shadow-2xl">
						<div className="flex items-center justify-between border-b border-slate-200 px-4 py-3">
							<div className="flex items-center gap-2 text-lg font-semibold text-slate-900">
								<FiInfo /> Graph details
							</div>
							<button
								type="button"
								onClick={() => setShowDetails(false)}
								className="rounded-full p-2 text-slate-500 transition hover:bg-slate-100 hover:text-slate-700"
								aria-label="Close details"
							>
								<FiX />
							</button>
						</div>
						<div className="max-h-[75vh] overflow-y-auto p-6">{detailsPanel}</div>
					</div>
				</div>
			)}
		</div>
	);
};

export default ConnectionsGraph;

